import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import apiClient from "../../services/axiosInterceptor";

/*
UPLOAD + ANALYZE
*/
export const uploadAndAnalyzeThunk = createAsyncThunk(
  "smartMerge/uploadAnalyze",
  async ({ file1, file2, onUploadProgress }, { rejectWithValue, signal }) => {
    try {
      const formData = new FormData();
      formData.append("file1", file1);
      formData.append("file2", file2);

      const response = await apiClient.post(
        "/file_merging/clean-and-upload",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress,
          signal,
        }
      );

      return response.data;

    } catch (error) {
      if (error?.name === 'CanceledError' || error?.message === 'canceled') {
        throw error;
      }
      return rejectWithValue(
        error.response?.data?.message || "File analysis failed"
      );
    }
  }
);


/*
MERGE FILES
*/
export const mergeFilesThunk = createAsyncThunk(
  "smartMerge/mergeFiles",
  async ({ endpoint, payload }, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(endpoint, payload);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.detail?.suggestion ||
        error.response?.data?.detail || 
        error.response?.data?.message || 
        "Merge failed"
      );
    }
  }
);
const smartMergeSlice = createSlice({
  name: "smartMerge",
  initialState: {
    file1: null,
    file2: null,
    uploadProgress: 0,
    activeTab: "join",
    appendMapping: [],

    fileUrl1: "",
    fileUrl2: "",

    file1Columns: [],
    file2Columns: [],
    commonColumns: [],

    selectedColumns1: [],
    selectedColumns2: [],

    joinKeys: [{ key1: null, key2: null }],
    joinType: "Inner",

    mergedFileUrl: null,
    blobFilePath: null,

    uploading: false,
    merging: false,

    analysisComplete: false,

    smartMergeMaxFileSize: null,

    error: null,
  },

  reducers: {
    setFile1: (state, action) => {
      state.file1 = action.payload;
    },

    setFile2: (state, action) => {
      state.file2 = action.payload;
    },

    setUploadProgress: (state, action) => {
      state.uploadProgress = action.payload;
    },

    setActiveTab: (state, action) => {
      state.activeTab = action.payload;
    },

    setAppendMapping: (state, action) => {
      state.appendMapping = action.payload;
    },

    setJoinKeys: (state, action) => {
      state.joinKeys = action.payload;
    },

    setJoinType: (state, action) => {
      state.joinType = action.payload;
    },

    setSelectedColumns1: (state, action) => {
      state.selectedColumns1 = action.payload;
    },

    setSelectedColumns2: (state, action) => {
      state.selectedColumns2 = action.payload;
    },

    setMergedFileUrl: (state, action) => {
      state.mergedFileUrl = action.payload;
    },

    setBlobFilePath: (state, action) => {
      state.blobFilePath = action.payload;
    },

    setSmartMergeMaxFileSize: (state, action) => {
      state.smartMergeMaxFileSize = action.payload;
    },

    resetAnalysis: (state) => {
      state.fileUrl1 = "";
      state.fileUrl2 = "";
      state.file1Columns = [];
      state.file2Columns = [];
      state.commonColumns = [];
      state.selectedColumns1 = [];
      state.selectedColumns2 = [];
      state.joinKeys = [{ key1: null, key2: null }];
      state.analysisComplete = false;
      state.mergedFileUrl = null;
      state.blobFilePath = null;
      state.uploadProgress = 0;
      state.activeTab = "join";
      state.appendMapping = [];
      state.smartMergeMaxFileSize = null;
    },
  },

  extraReducers: (builder) => {
    /*
    Upload + Analyze
    */
    builder
      .addCase(uploadAndAnalyzeThunk.pending, (state) => {
        state.uploading = true;
      })

      .addCase(uploadAndAnalyzeThunk.fulfilled, (state, action) => {
        const { file1, file2, common_columns } = action.payload;

        const cols1 = file1.columns_metadata.map((c) => c.name);
        const cols2 = file2.columns_metadata.map((c) => c.name);

        state.fileUrl1 = file1.blob_path;
        state.fileUrl2 = file2.blob_path;

        state.file1Columns = cols1;
        state.file2Columns = cols2;
        state.commonColumns = common_columns;

        state.selectedColumns1 = cols1;
        state.selectedColumns2 = cols2;

        state.joinKeys = common_columns?.length
          ? [{ key1: common_columns[0], key2: common_columns[0] }]
          : [{ key1: null, key2: null }];

        state.analysisComplete = true;
        state.uploading = false;
      })

      .addCase(uploadAndAnalyzeThunk.rejected, (state, action) => {
        state.uploading = false;
        state.error = action.payload;
      });

    /*
    Merge Files
    */
    builder
      .addCase(mergeFilesThunk.pending, (state) => {
        state.merging = true;
        state.mergedFileUrl = null;
        state.blobFilePath = null;
      })

      .addCase(mergeFilesThunk.fulfilled, (state, action) => {
        const downloadUrl =
          action.payload?.merged_file?.download_url ||
          action.payload?.appended_file?.download_url ||
          action.payload?.mergedFileUrl;

        const blobPath =
          action.payload?.merged_file?.blob_path ||
          action.payload?.appended_file?.blob_path ||
          action.payload?.blobFilePath;

        state.mergedFileUrl = downloadUrl;
        state.blobFilePath = blobPath;
        state.merging = false;
      })

      .addCase(mergeFilesThunk.rejected, (state, action) => {
        state.merging = false;
        state.error = action.payload;
      });
  },
});

export const {
  setFile1,
  setFile2,
  setUploadProgress,
  setActiveTab,
  setAppendMapping,
  setJoinKeys,
  setJoinType,
  setSelectedColumns1,
  setSelectedColumns2,
  resetAnalysis,
  setMergedFileUrl,
  setBlobFilePath,
  setSmartMergeMaxFileSize 
} = smartMergeSlice.actions;

export default smartMergeSlice.reducer;